import React, { useEffect, Component } from 'react';
import { X, AlertTriangle, Package, MapPin, CheckCircle } from 'lucide-react';
import { Product, WA_NUMBER } from '../data/products';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
interface ProductModalProps {
  product: Product | null;
  onClose: () => void;
}
export function ProductModal({
  product,
  onClose
}: ProductModalProps) {
  // Lock body scroll when open
  useEffect(() => {
    if (product) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [product]);
  // Close on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  if (!product) return null;
  const waMsg = encodeURIComponent(`Hola! Quiero el ${product.name} con 20% OFF — precio $${product.currentPrice} UY. ¿Tienen stock disponible?`);
  const waLink = `https://wa.me/${WA_NUMBER}?text=${waMsg}`;
  return <div className="fixed inset-0 bg-black/90 z-[9999] overflow-y-auto p-[30px_20px] backdrop-blur-sm flex items-start justify-center" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-402" data-mp="1qcmyawhidcho">
      <div className="bg-dark-600 border border-[#333] rounded-xl max-w-[780px] w-full overflow-hidden relative m-auto animate-modal-in" data-id="element-403" data-mp="1heb7atcqccop">
        <button onClick={onClose} className="absolute top-[15px] right-[20px] bg-transparent border-none text-white text-[2rem] cursor-pointer z-20 leading-none transition-colors duration-200 hover:text-alphaGold" aria-label="Close modal" data-id="element-404" data-mp="17ekhv6310nvh">
          <X size={32} data-id="element-405" data-mp="jo6jkxtulssg" />
        </button>

        <div className="w-full h-[320px] bg-white flex items-center justify-center overflow-hidden" data-id="element-406" data-mp="jp8927bm2htp">
          <img src={product.image} alt={product.name} className={`max-w-full max-h-full object-contain p-[30px] ${product.imageZoom ? 'scale-[1.25]' : ''}`} style={product.imageCropPercent ? {
          clipPath: `inset(0 ${product.imageCropPercent}% 0 ${product.imageCropPercent}%)`
        } : undefined} onError={e => {
          e.currentTarget.src = product.fallbackImage;
        }} data-id="element-407" data-mp="on9sitcy8j5p" />
        </div>

        <div className="p-[35px]" data-id="element-408" data-mp="359zla5py0e23">
          <div className="inline-block bg-alphaGold text-white text-[0.75rem] font-extrabold py-1 px-3.5 rounded-sm tracking-[2px] uppercase mb-[15px]" data-id="element-409" data-mp="35ar9uwl4hbiv">
            20% OFF — OFERTA LIMITADA
          </div>

          <h2 className="text-[1.6rem] font-black uppercase tracking-[2px] text-white mb-[15px]" data-id="element-410" data-mp="2vbti7kseh1vn">
            {product.name}
          </h2>

          <div className="bg-alphaGold/10 border-l-4 border-l-alphaGold p-[18px_20px] mb-5 rounded-r" data-id="element-411" data-mp="1jwdbmvah9qas">
            <h4 className="text-alphaGold text-[0.85rem] uppercase tracking-[2px] mb-2 font-bold flex items-center gap-2" data-id="element-412" data-mp="19thqpsn85u5d">
              <AlertTriangle className="w-4 h-4" data-id="element-413" data-mp="1esudkde3j17y" /> El Problema que Resuelve
            </h4>
            <p className="text-[#ccc] text-[0.92rem] leading-[1.7]" data-id="element-414" data-mp="1jq4b6gbn2j6v">
              {product.pain}
            </p>
          </div>

          <p className="text-[#aaa] leading-[1.8] text-[0.97rem] mb-[25px]" data-id="element-415" data-mp="1oj6wy2a6coci">
            {product.description}
          </p>

          <p className="text-[0.85rem] uppercase tracking-[2px] text-[#666] mb-3 font-bold flex items-center gap-2" data-id="element-416" data-mp="1xik6euyatcx8">
            <CheckCircle className="w-4 h-4 text-alphaGold" data-id="element-417" data-mp="22h3ub0wgcys8" /> BENEFICIOS CLAVE
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-[25px]" data-id="element-418" data-mp="27ggefdvxvrr4">
            {product.benefits.map((benefit, idx) => <div key={idx} className="flex items-start gap-2.5 bg-[#1e1e1e] p-3 rounded" data-id="element-419" data-mp="84xj423k6hzx" data-mpi={idx}>
                <span className="text-alphaGold font-black text-[1rem] flex-shrink-0 mt-px" data-id="element-420" data-mp="d4jwqcnau099">
                  ✓
                </span>
                <span className="text-[#ccc] text-[0.85rem] leading-[1.5]" data-id="element-421" data-mp="1yl9aettxkase">
                  {benefit}
                </span>
              </div>)}
          </div>

          <div className="flex items-center gap-5 mb-[25px] flex-wrap" data-id="element-422" data-mp="27leeatzx0r1t">
            <span className="text-[2.5rem] font-black text-alphaGold" data-id="element-423" data-mp="27m7gdhnxisdm">
              ${product.currentPrice.toLocaleString('es-UY')}
            </span>
          </div>

          <div className="bg-alphaGold/10 border border-alphaGold/25 rounded p-[14px_18px] mb-5 text-[0.88rem] text-alphaGold font-semibold text-center flex items-center justify-center gap-2" data-id="element-424" data-mp="1fv9pmj54hhus">
            <AlertTriangle className="w-4 h-4" data-id="element-425" data-mp="1otvd8spgbn2f" /> Stock limitado · Esta oferta
            puede terminar en cualquier momento
          </div>

          <div className="bg-dark-500 border border-dark-300 rounded p-[15px_18px] mb-5 text-[0.85rem] text-[#999] space-y-2" data-id="element-426" data-mp="11394ez7mhyrd">
            <div className="flex items-start gap-2" data-id="element-427" data-mp="37tuknhs897a0">
              <Package className="w-4 h-4 mt-0.5 text-alphaGold flex-shrink-0" data-id="element-428" data-mp="37sb5juib529t" />
              <span data-id="element-429" data-mp="3cl4b2klloemy">
                <strong className="text-white" data-id="element-430" data-mp="3clm4zaksigrp">Envío a domicilio:</strong> desde
                $190 pesos uruguayos, varía según tu zona
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-431" data-mp="33sslf0yb8v81">
              <MapPin className="w-4 h-4 mt-0.5 text-alphaGold flex-shrink-0" data-id="element-432" data-mp="3rhlij0qo5948" />
              <span data-id="element-433" data-mp="3rfh4vr2ywvqr">
                <strong className="text-white" data-id="element-434" data-mp="3vix1inf1ew3g">Pick Up:</strong> Malvín,
                Montevideo
              </span>
            </div>
          </div>

          <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-whatsapp text-white text-[1.05rem] font-extrabold py-[18px] px-[30px] rounded uppercase tracking-[1px] transition-all duration-300 w-full shadow-[0_6px_25px_rgba(37,211,102,0.3)] hover:bg-whatsappDark hover:scale-[1.02]" data-id="element-435" data-mp="3hiw0n7yrvonz">
            <WhatsAppIcon className="w-6 h-6" data-id="element-436" data-mp="1r0pdkyyvn36b" />
            Quiero Este Producto — Pedir por WhatsApp
          </a>
        </div>
      </div>
    </div>;
}